import { IconCustomGoldPremium } from 'components/icons'
import { DuoLayoutArrange, DuoSettings1, DuoUser } from 'components/icons/duotone'
import { Building2, Landmark, Trophy, Users, User } from 'lucide-react'
import { FaPhoneAlt } from 'react-icons/fa'
import { IoInformationCircle } from 'react-icons/io5'

/**
 * @param {string} basePath — segment root without trailing slash, e.g. `/en` or `/test`
 */
export function getHostNavLinks(basePath) {
    const b = basePath.replace(/\/$/, '')
    return [
        { name: 'Home', href: b },
        { name: 'Hierarchy', href: `${b}/hierarchy` },
        { name: 'Coming Soon', href: `${b}/coming-soon` }
    ]
}
export function getHostGuestKeyNavLinks(basePath) {
    const b = basePath.replace(/\/$/, '')
    return [
        { key: 'home', href: b },
        { key: 'hierarchy', href: `${b}/hierarchy` },
        { key: 'organisations', href: `${b}/explore/organisations` },
        { key: 'federations', href: `${b}/explore/federations` },
        { key: 'competitions', href: `${b}/explore/competitions` },
        { key: 'teams', href: `${b}/explore/teams` },
        { key: 'rankings', href: `${b}/rankings` }
    ]
}
export function getHostLoggedInKeyNavLinks(basePath) {
    const b = basePath.replace(/\/$/, '')
    return [
        { key: 'playerHub', href: `${b}/home`, icon: <DuoLayoutArrange className='inline w-7 h-7 aspect-square' /> },
        { key: 'admin', href: `${b}/admin`, icon: <DuoUser className='inline w-7 h-7 aspect-square' /> },
        { key: 'profile', href: `${b}/home/profile`, icon: <DuoSettings1 className='inline w-7 h-7 aspect-square' /> },
        { key: 'premium', href: `${b}/premium`, icon: <IconCustomGoldPremium /> },
        {
            key: 'aboutUs',
            href: `${b}/info/about-us`,
            icon: <IoInformationCircle className='inline size-6 aspect-square' />
        },
        { key: 'contactUs', href: `${b}/info/contact-us`, icon: <FaPhoneAlt className='inline size-4 aspect-square' /> }
    ]
}

const nodes = [
    {
        icon: Building2,
        title: 'Organization',
        desc: 'Full ownership and oversight of the entire ecosystem. Manage global branding and data.',
        subText:
            'An Organisation is the governing body of a competitive ecosystem. Think of it like PFL — a company or entity that controls all competitions, rules, and access within its platform. The Organisation admin creates and manages everything from the top down.',
        subPoints: [
            'Creates and configures all Federations',
            'Sets global rules, branding, and access levels',
            'Can run competitions directly, or delegate to Federations'
        ]
    },
    {
        icon: Landmark,
        title: 'Federations',
        desc: 'Regional governing bodies that manage local rules and sanctioned events.',
        subText:
            'A Federation is a subdivision created by the Organisation to manage a specific region, country, or community — for example, a French Federation or Italian Federation. This allows large organisations to delegate control while maintaining a unified structure.',
        subPoints: [
            'Scoped to a region, country, or community',
            'Can host its own competitions under the Organisation umbrella',
            'Managed by a federation admin appointed by the Organisation'
        ]
    },
    {
        icon: Trophy,
        title: 'Competitions',
        desc: 'Specific leagues or tournaments with independent standings and brackets.',
        subText:
            'A Competition is a league or cup created within a Federation (or directly under the Organisation). This is the actual event teams register for and play in — with fixtures, standings, rules, and a season structure.',
        subPoints: [
            'Configurable as a league, cup, or group-stage format',
            'Teams apply or are invited to join',
            'Manages fixtures, results, standings, and rules'
        ]
    },
    {
        icon: Users,
        title: 'Teams',
        desc: 'Squads belonging to competitions. Manage rosters and stats.',
        subText:
            'A Team is an EA FC 26 Pro Club registered on Disporty. Teams are made up of players and can participate in multiple competitions simultaneously. Team managers handle registration, squad selection, and communication.',
        subPoints: [
            'Managed by a captain or team admin',
            'Can enter multiple competitions at once',
            'Players are linked individually to the squad'
        ]
    },
    {
        icon: User,
        title: 'Players',
        desc: 'Individual athlete profiles with historical performance data.',
        subText:
            'A Player is an individual EA FC 26 Pro Clubs user with their own profile on Disporty. Players join or are invited to a Team, and through their Team they participate in Competitions. Stats, history, and identity all live at the player level.',
        subPoints: [
            'Own personal profile with stats and match history',
            'Can be part of one or multiple teams',
            'Connects directly to EA FC 26 Pro Clubs identity'
        ]
    }
]

export { nodes }
